/**
 * PartnershipDrawer — right-side slide-out application form
 * Opens for: "Apply to Be a Guest Speaker" (Free), "Strategic Alliance Partner" (Annual Fee), "Experience Partnership" (Annual Fee)
 * Obsidian Pulse design: dark panel, tier-colored accents, smooth CSS transition.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Send,
  CheckCheck,
  Mic,
  Star,
  UtensilsCrossed,
  LoaderCircle,
} from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { type SpeakerProfileErrors, validateSpeakerProfile } from "@/lib/speakerProfileValidation";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";

export type PartnershipTier = "speaker" | "alliance" | "dinner" | null;

const TIER_NOTIFICATION_KIND = {
  speaker: "collaboration_speaker",
  alliance: "collaboration_alliance",
  dinner: "collaboration_dinner",
} as const;

export function buildPartnershipSubmission(
  tier: NonNullable<PartnershipTier>,
  values: Record<string, string>,
  message: string,
  website = "",
) {
  const input = {
    kind: TIER_NOTIFICATION_KIND[tier],
    fullName: values["Full Name"]?.trim() ?? "",
    email: values["Email Address"]?.trim() ?? "",
    message: message.trim(),
    sourcePage: "/collaborate-with-us",
    website,
  };

  if (tier === "speaker") {
    return {
      ...input,
      organization: values["Organization / Title"]?.trim() || undefined,
      topic: values["Proposed Topic"]?.trim() || undefined,
    };
  }

  return {
    ...input,
    organization: values["Company Name"]?.trim() || undefined,
    title: values["Role / Title"]?.trim() || undefined,
  };
}

interface TierConfig {
  icon: React.ReactNode;
  label: string;
  color: string;
  subject: string;
  fields: string[];
  placeholder: string;
}

const TIER_CONFIG: Record<NonNullable<PartnershipTier>, TierConfig> = {
  speaker: {
    icon: <Mic size={18} />,
    label: "Apply to Be a Guest Speaker",
    color: "#A855F7",
    subject: "Speaker Application",
    fields: ["Full Name", "Email Address", "Phone Number", "Organization", "Professional Title", "LinkedIn Profile URL", "Proposed Talk Topic"],
    placeholder: "Share a complete professional biography, including your experience and perspective for the CHI Alliance community...",
  },
  alliance: {
    icon: <Star size={18} />,
    label: "Strategic Alliance Partner",
    color: "#00D4FF",
    subject: "Strategic Alliance Partner Application",
    fields: ["Full Name", "Email Address", "Company Name", "Role / Title"],
    placeholder: "Share your partnership goals, what your organization does, and how you envision collaborating with the CHI Alliance...",
  },
  dinner: {
    icon: <UtensilsCrossed size={18} />,
    label: "Experience Partnership",
    color: "#F5A623",
    subject: "Experience Partnership Application",
    fields: ["Full Name", "Email Address", "Company Name", "Role / Title"],
    placeholder: "Tell us about your goals for the experience and the type of executive guests you'd like to connect with...",
  },
};

const SPEAKER_FIELD_BY_LABEL = {
  "Full Name": "fullName",
  "Email Address": "email",
  "Phone Number": "phone",
  Organization: "organization",
  "Professional Title": "professionalTitle",
  "LinkedIn Profile URL": "linkedinUrl",
  "Proposed Talk Topic": "proposedTopic",
} as const;

interface PartnershipDrawerProps {
  tier: PartnershipTier;
  onClose: () => void;
}

export default function PartnershipDrawer({ tier, onClose }: PartnershipDrawerProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [speakerConsent, setSpeakerConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [portrait, setPortrait] = useState<{ dataUrl: string; fileName: string; consent: boolean } | null>(null);
  const [portraitError, setPortraitError] = useState("");
  const [speakerFieldErrors, setSpeakerFieldErrors] = useState<SpeakerProfileErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const submitMutation = trpc.notifications.submit.useMutation();
  const speakerSubmitMutation = trpc.speakers.submitApplication.useMutation();

  const config = tier ? TIER_CONFIG[tier] : null;

  // Animate in
  useEffect(() => {
    if (tier) {
      setSubmitted(false);
      setValues({});
      setMessage("");
      setWebsite("");
      setSpeakerConsent(false);
      setMarketingConsent(false);
      setPortrait(null);
      setPortraitError("");
      setSpeakerFieldErrors({});
      requestAnimationFrame(() => setVisible(true));
      const focusTimer = window.setTimeout(() => firstInputRef.current?.focus(), 360);
      return () => window.clearTimeout(focusTimer);
    } else {
      setVisible(false);
    }
  }, [tier]);

  useEffect(() => {
    if (!tier) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [tier]);

  function handleClose() {
    setVisible(false);
    setTimeout(onClose, 320);
  }

  function handlePortraitChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      setPortrait(null);
      setPortraitError("");
      return;
    }
    if (!/image\/(png|jpeg|webp)/.test(file.type)) {
      setPortrait(null);
      setPortraitError("Choose a PNG, JPEG, or WebP portrait.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setPortrait(null);
      setPortraitError(`Portraits must be ${MAX_IMAGE_UPLOAD_LABEL} or smaller.`);
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        setPortraitError("The portrait could not be read. Please try another file.");
        return;
      }
      setPortrait({ dataUrl: reader.result, fileName: file.name, consent: false });
      setPortraitError("");
    };
    reader.onerror = () => setPortraitError("The portrait could not be read. Please try another file.");
    reader.readAsDataURL(file);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tier || submitMutation.isPending || speakerSubmitMutation.isPending) return;

    if (tier === "speaker") {
      const errors = validateSpeakerProfile({
        fullName: values["Full Name"] ?? "",
        email: values["Email Address"] ?? "",
        phone: values["Phone Number"] ?? "",
        organization: values.Organization ?? "",
        professionalTitle: values["Professional Title"] ?? "",
        bio: message,
        linkedinUrl: values["LinkedIn Profile URL"] ?? "",
        proposedTopic: values["Proposed Talk Topic"] ?? "",
        consentToContact: speakerConsent,
      });
      const firstInvalidField = Object.keys(errors)[0] as keyof SpeakerProfileErrors | undefined;
      if (firstInvalidField) {
        setSpeakerFieldErrors(errors);
        const controlId = firstInvalidField === "bio"
          ? "partnership-speaker-message"
          : firstInvalidField === "consentToContact"
            ? "partnership-speaker-consent"
            : `partnership-speaker-${Object.entries(SPEAKER_FIELD_BY_LABEL).find(([, value]) => value === firstInvalidField)?.[0].toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
        requestAnimationFrame(() => document.getElementById(controlId)?.focus());
        return;
      }
      setSpeakerFieldErrors({});
    }

    const notificationId = `collaboration-${tier}-submission`;
    chiNotify.loading("Sending your application…", {
      id: notificationId,
      duration: Number.POSITIVE_INFINITY,
    });

    if (tier === "speaker") {
      speakerSubmitMutation.mutate(
        {
          fullName: values["Full Name"]?.trim() ?? "",
          email: values["Email Address"]?.trim() ?? "",
          phone: values["Phone Number"]?.trim() ?? "",
          organization: values["Organization"]?.trim() ?? "",
          professionalTitle: values["Professional Title"]?.trim() ?? "",
          bio: message.trim(),
          linkedinUrl: values["LinkedIn Profile URL"]?.trim() ?? "",
          proposedTopic: values["Proposed Talk Topic"]?.trim() ?? "",
          consentToContact: speakerConsent,
          marketingConsent,
          portrait: portrait?.consent === true
            ? { dataUrl: portrait.dataUrl, fileName: portrait.fileName, consent: true as const }
            : undefined,
        },
        {
          onSuccess: () => {
            setSubmitted(true);
            chiNotify.success("Application received", {
              id: notificationId,
              description: "Your speaker profile is now pending administrator review.",
            });
          },
          onError: error => {
            chiNotify.error("We couldn’t send your application", {
              id: notificationId,
              description: error.message || "Please review every required field and try again.",
            });
          },
        },
      );
      return;
    }

    submitMutation.mutate(
      buildPartnershipSubmission(tier, values, message, website),
      {
        onSuccess: () => {
          setSubmitted(true);
          chiNotify.success("Application received", {
            id: notificationId,
            description:
              "Your information was delivered securely to The Chi Alliance team.",
          });
        },
        onError: error => {
          chiNotify.error("We couldn’t send your application", {
            id: notificationId,
            description:
              error.message || "Please review your details and try again.",
          });
        },
      },
    );
  }

  if (!tier || !config) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 999,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partnership-drawer-title"
        aria-busy={submitMutation.isPending || speakerSubmitMutation.isPending}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(480px, 100vw)",
          background: "#0D1220",
          borderLeft: `1px solid ${config.color}30`,
          boxShadow: `-8px 0 60px rgba(0,0,0,0.6), -1px 0 0 ${config.color}20`,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.32s cubic-bezier(0.23, 1, 0.32, 1)",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.5rem 1.75rem 1.25rem",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
            flexShrink: 0,
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.3rem 0.875rem",
                background: `${config.color}15`,
                border: `1px solid ${config.color}40`,
                borderRadius: "100px",
                color: config.color,
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: "0.625rem",
              }}
            >
              {config.icon}
              {tier === "speaker" ? "Guest speaker" : "Annual Fee"}
            </div>
            <h2
              id="partnership-drawer-title"
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.5rem",
                color: "white",
                letterSpacing: "0.04em",
                margin: 0,
                lineHeight: 1.1,
              }}
            >
              {config.label}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close application form"
            onClick={handleClose}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "0.5rem",
              color: "#64748B",
              cursor: "pointer",
              padding: "0.5rem",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "white"; e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#64748B"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "1.75rem", overflowY: "auto" }}>
          {submitted ? (
            /* Success state */
            <div style={{ textAlign: "center", paddingTop: "3rem" }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `${config.color}18`,
                  border: `2px solid ${config.color}50`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.5rem",
                }}
              >
                <CheckCheck size={28} style={{ color: config.color }} />
              </div>
              <h3 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontSize: "1.42rem", fontWeight: 700, color: "white", marginBottom: "0.75rem", letterSpacing: "-0.04em" }}>
                Application Received!
              </h3>
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.75, maxWidth: "320px", margin: "0 auto 2rem" }}>
                {tier === "speaker"
                  ? "Your speaker profile is pending administrator review. You will receive an email once there is an update; speaker-portal access is not available until approval."
                  : "Thank you for your interest. A member of The CHI Alliance team will be in touch within 2–3 business days to explore next steps."}
              </p>
              <button
                onClick={handleClose}
                style={{
                  padding: "0.75rem 2rem",
                  background: `${config.color}18`,
                  border: `1px solid ${config.color}40`,
                  borderRadius: "0.625rem",
                  color: config.color,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.825rem",
                  letterSpacing: "0.04em",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                Close
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} noValidate={tier === "speaker"}>
              <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.7, marginBottom: "1.75rem" }}>
                {tier === "speaker"
                  ? "Complete every field below. Your application is reviewed by The CHI Alliance before speaker-portal access is granted."
                  : "Complete the form below and a member of The CHI Alliance team will reach out to discuss how we can collaborate."}
              </p>
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", margin: "-1rem 0 1.25rem" }}><span aria-hidden="true" style={{ color: config.color, fontWeight: 700 }}>*</span> Required field</p>

              {config.fields.map((field, index) => {
                const fieldId = `partnership-${tier}-${field.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                const speakerField = tier === "speaker"
                  ? SPEAKER_FIELD_BY_LABEL[field as keyof typeof SPEAKER_FIELD_BY_LABEL]
                  : undefined;
                const error = speakerField ? speakerFieldErrors[speakerField] : undefined;
                return (
                <div key={field} style={{ marginBottom: "1.25rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".2rem", marginBottom: "0.4rem" }}>
                    <label
                      htmlFor={fieldId}
                      style={{
                        display: "block",
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: error ? "#FDA4AF" : "#64748B",
                      }}
                    >
                      {field}
                    </label>
                    <span aria-hidden="true" style={{ color: config.color }}>*</span>
                  </div>
                  <input
                    id={fieldId}
                    ref={index === 0 ? firstInputRef : undefined}
                    type={field.toLowerCase().includes("email") ? "email" : field.includes("LinkedIn") ? "url" : "text"}
                    required
                    aria-required="true"
                    minLength={field === "Phone Number" ? 7 : undefined}
                    pattern={field.includes("LinkedIn") ? "https://(www.)?linkedin.com/in/[A-Za-z0-9_-]+/?" : undefined}
                    title={field.includes("LinkedIn") ? "Use a public https://www.linkedin.com/in/{profile} URL." : undefined}
                    maxLength={field.toLowerCase().includes("email") ? 320 : 180}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? `${fieldId}-error` : undefined}
                    value={values[field] ?? ""}
                    onChange={(e) => {
                      setValues((v) => ({ ...v, [field]: e.target.value }));
                      if (speakerField) {
                        setSpeakerFieldErrors(current => {
                          const { [speakerField]: _cleared, ...remaining } = current;
                          return remaining;
                        });
                      }
                    }}
                    placeholder={`Enter your ${field.toLowerCase()}`}
                    style={{
                      width: "100%",
                      padding: "0.75rem 1rem",
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${error ? "rgba(251,113,133,.75)" : "rgba(255,255,255,0.1)"}`,
                      borderRadius: "0.5rem",
                      color: "white",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.875rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = `${config.color}60`)}
                    onBlur={(e) => (e.target.style.borderColor = error ? "rgba(251,113,133,.75)" : "rgba(255,255,255,0.1)")}
                  />
                  {error && <p id={`${fieldId}-error`} role="alert" style={{ margin: "0.35rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{error}</p>}
                </div>
                );
              })}

              {/* Message */}
              <div style={{ marginBottom: "1.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".2rem", marginBottom: "0.4rem" }}>
                  <label
                    htmlFor={`partnership-${tier}-message`}
                    style={{
                      display: "block",
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: speakerFieldErrors.bio ? "#FDA4AF" : "#64748B",
                    }}
                  >
                    {tier === "speaker" ? "Professional Biography" : "Tell Us More"}
                  </label>
                  <span aria-hidden="true" style={{ color: config.color }}>*</span>
                </div>
                <textarea
                  id={`partnership-${tier}-message`}
                  required
                  aria-required="true"
                  minLength={tier === "speaker" ? 40 : 10}
                  maxLength={tier === "speaker" ? 8000 : 4000}
                  aria-invalid={tier === "speaker" && Boolean(speakerFieldErrors.bio)}
                  aria-describedby={tier === "speaker" && speakerFieldErrors.bio ? `partnership-${tier}-message-error` : undefined}
                  rows={5}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (tier === "speaker") {
                      setSpeakerFieldErrors(current => {
                        const { bio: _cleared, ...remaining } = current;
                        return remaining;
                      });
                    }
                  }}
                  placeholder={config.placeholder}
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${tier === "speaker" && speakerFieldErrors.bio ? "rgba(251,113,133,.75)" : "rgba(255,255,255,0.1)"}`,
                    borderRadius: "0.5rem",
                    color: "white",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    outline: "none",
                    resize: "vertical",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = `${config.color}60`)}
                  onBlur={(e) => (e.target.style.borderColor = tier === "speaker" && speakerFieldErrors.bio ? "rgba(251,113,133,.75)" : "rgba(255,255,255,0.1)")}
                />
                {tier === "speaker" && speakerFieldErrors.bio && <p id={`partnership-${tier}-message-error`} role="alert" style={{ margin: "0.35rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{speakerFieldErrors.bio}</p>}
              </div>

              {tier === "speaker" && (
                <>
                <div style={{ margin: "0 0 1.25rem" }}>
                  <label htmlFor="partnership-speaker-portrait" style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B", marginBottom: "0.4rem" }}>
                    Portrait for review <span style={{ color: "#475569", fontWeight: 500 }}>(optional)</span>
                  </label>
                  <input
                    id="partnership-speaker-portrait"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handlePortraitChange}
                    aria-describedby="partnership-speaker-portrait-help"
                    style={{ width: "100%", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "0.8rem" }}
                  />
                  <p id="partnership-speaker-portrait-help" style={{ margin: "0.4rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.74rem", lineHeight: 1.45 }}>
                    PNG, JPEG, or WebP; up to {MAX_IMAGE_UPLOAD_LABEL}. It is shared only with the CHI Alliance review team when you consent below.
                  </p>
                  {portraitError && <p role="alert" style={{ margin: "0.35rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{portraitError}</p>}
                  {portrait && (
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", margin: "0.75rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={portrait.consent}
                        onChange={event => setPortrait(current => current ? { ...current, consent: event.target.checked } : current)}
                        style={{ marginTop: "0.15rem", accentColor: config.color }}
                      />
                      <span>I consent to The CHI Alliance storing this portrait for the review of my speaker application.</span>
                    </label>
                  )}
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", margin: "0 0 .4rem", color: speakerFieldErrors.consentToContact ? "#FDA4AF" : "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55, cursor: "pointer" }}>
                  <input
                    id="partnership-speaker-consent"
                    type="checkbox"
                    required
                    aria-invalid={Boolean(speakerFieldErrors.consentToContact)}
                    aria-describedby={speakerFieldErrors.consentToContact ? "partnership-speaker-consent-error" : undefined}
                    checked={speakerConsent}
                    onChange={event => {
                      setSpeakerConsent(event.target.checked);
                      setSpeakerFieldErrors(current => {
                        const { consentToContact: _cleared, ...remaining } = current;
                        return remaining;
                      });
                    }}
                    style={{ marginTop: "0.15rem", accentColor: config.color }}
                  />
                  <span>I consent to The CHI Alliance using the information in this application to review my speaker profile and contact me about it.</span>
                </label>
                {speakerFieldErrors.consentToContact && <p id="partnership-speaker-consent-error" role="alert" style={{ margin: "0 0 1.75rem", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{speakerFieldErrors.consentToContact}</p>}
                {!speakerFieldErrors.consentToContact && <div style={{ height: "0.75rem" }} aria-hidden="true" />}
                <label style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", margin: "0 0 1.75rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={event => setMarketingConsent(event.target.checked)}
                    style={{ marginTop: "0.15rem", accentColor: config.color }}
                  />
                  <span>I’d like to receive occasional CHI Alliance updates. This is optional and does not affect my application.</span>
                </label>
                </>
              )}

              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}
              >
                <label htmlFor={`partnership-${tier}-website`}>Website</label>
                <input
                  id={`partnership-${tier}-website`}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={event => setWebsite(event.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={submitMutation.isPending || speakerSubmitMutation.isPending}
                style={{
                  width: "100%",
                  padding: "0.9rem",
                  background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`,
                  border: "none",
                  borderRadius: "0.625rem",
                  color: tier === "speaker" ? "white" : "#080C18",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  cursor: submitMutation.isPending || speakerSubmitMutation.isPending ? "wait" : "pointer",
                  opacity: submitMutation.isPending || speakerSubmitMutation.isPending ? 0.72 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  boxShadow: `0 6px 20px ${config.color}40`,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${config.color}55`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 6px 20px ${config.color}40`; }}
              >
                {submitMutation.isPending || speakerSubmitMutation.isPending ? (
                  <><LoaderCircle className="chi-notification-spinner" size={15} /> Sending securely…</>
                ) : (
                  <><Send size={15} /> Submit Application</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
